import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';

interface Assessor {
  id: number;
  nome: string;
  dataNascimento: string;
  cpf: string;
  rg: string;
  email: string;
  confirmacaoEmail: string;
  senha: string;
  confirmacaoSenha: string;
  cep: string;
  rua: string;
  numero: string;
  bairro: string;
  complemento: string;
  pais: string;
  estado: string;
  cidade: string;
  telefoneCelular: string;
  telefoneAdicional: string;
  observacoes: string;
}

@Component({
  selector: 'app-cadastro-assessor',
  templateUrl: './cadastro-assessor.component.html',
  styleUrls: ['./cadastro-assessor.component.scss']
})
export class CadastroAssessorComponent implements OnInit {

  formAssessor: FormGroup;

  constructor(private fb: FormBuilder) { }

  ngOnInit(): void {
    this.inicializarFormulario();
  }

  inicializarFormulario(): void {
    this.formAssessor = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3)]],
      dataNascimento: ['', Validators.required],
      cpf: ['', [Validators.required, Validators.pattern(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/)]],
      rg: ['', [Validators.required, Validators.minLength(5)]],
      email: ['', [Validators.required, Validators.email]],
      confirmacaoEmail: ['', [Validators.required, Validators.email]],
      senha: ['', [Validators.required, Validators.minLength(6)]],
      confirmacaoSenha: ['', [Validators.required, Validators.minLength(6)]],
      cep: ['', [Validators.required, Validators.pattern(/^\d{5}-\d{3}$/)]],
      rua: ['', [Validators.required, Validators.minLength(3)]],
      numero: ['', [Validators.required, Validators.minLength(1)]],
      bairro: ['', [Validators.required, Validators.minLength(3)]],
      complemento: [''],
      pais: ['', Validators.required],
      estado: ['', Validators.required],
      cidade: ['', [Validators.required, Validators.minLength(3)]],
      telefoneCelular: ['', [Validators.required, Validators.pattern(/^\(\d{2}\) \d{5}-\d{4}$/)]],
      telefoneAdicional: [''],
      observacoes: ['']
    }, { validators: [this.emailMatchValidator, this.senhaMatchValidator] });
  }

  // Validador personalizado para verificar se os emails coincidem
  emailMatchValidator(group: AbstractControl): ValidationErrors | null {
    const email = group.get('email');
    const confirmacaoEmail = group.get('confirmacaoEmail');
    
    if (email && confirmacaoEmail && email.value !== confirmacaoEmail.value) {
      confirmacaoEmail.setErrors({ emailMismatch: true });
      return { emailMismatch: true };
    }
    
    if (confirmacaoEmail && confirmacaoEmail.errors && confirmacaoEmail.errors['emailMismatch']) {
      delete confirmacaoEmail.errors['emailMismatch'];
      if (Object.keys(confirmacaoEmail.errors).length === 0) {
        confirmacaoEmail.setErrors(null);
      }
    }
    
    return null;
  }

  // Validador personalizado para verificar se as senhas coincidem
  senhaMatchValidator(group: AbstractControl): ValidationErrors | null {
    const senha = group.get('senha');
    const confirmacaoSenha = group.get('confirmacaoSenha');
    
    if (senha && confirmacaoSenha && senha.value !== confirmacaoSenha.value) {
      confirmacaoSenha.setErrors({ senhaMismatch: true });
      return { senhaMismatch: true };
    }
    
    if (confirmacaoSenha && confirmacaoSenha.errors && confirmacaoSenha.errors['senhaMismatch']) {
      delete confirmacaoSenha.errors['senhaMismatch'];
      if (Object.keys(confirmacaoSenha.errors).length === 0) {
        confirmacaoSenha.setErrors(null);
      }
    }
    
    return null;
  }

  onSubmit(): void {
    if (this.formAssessor.valid) {
      const assessorData = this.formAssessor.value;
      console.log('Dados do assessor:', assessorData);
      alert('Assessor cadastrado com sucesso!');
      this.limparFormulario();
    } else {
      this.marcarCamposInvalidos();
      alert('Por favor, preencha todos os campos obrigatórios corretamente.');
    }
  }

  limparFormulario(): void {
    this.formAssessor.reset();
  }

  marcarCamposInvalidos(): void {
    Object.keys(this.formAssessor.controls).forEach(key => {
      const control = this.formAssessor.get(key);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });
  }

  // Métodos auxiliares para validação
  isFieldInvalid(fieldName: string): boolean {
    const control = this.formAssessor.get(fieldName);
    return control ? control.invalid && control.touched : false;
  }

  getFieldError(fieldName: string): string {
    const control = this.formAssessor.get(fieldName);
    if (control?.errors) {
      if (control.errors['required']) {
        return 'Este campo é obrigatório.';
      }
      if (control.errors['minlength']) {
        return `Mínimo de ${control.errors['minlength'].requiredLength} caracteres.`;
      }
      if (control.errors['email']) {
        return 'Digite um email válido.';
      }
      if (control.errors['pattern']) {
        if (fieldName === 'cpf') {
          return 'Digite um CPF válido (000.000.000-00).';
        }
        if (fieldName === 'cep') {
          return 'Digite um CEP válido (00000-000).';
        }
        if (fieldName === 'telefoneCelular') {
          return 'Digite um telefone válido ((00) 00000-0000).';
        }
      }
      if (control.errors['emailMismatch']) {
        return 'Os emails não coincidem.';
      }
      if (control.errors['senhaMismatch']) {
        return 'As senhas não coincidem.';
      }
    }
    return '';
  }

  // Métodos auxiliares para verificar erros específicos
  hasEmailMismatchError(): boolean {
    return this.formAssessor.hasError('emailMismatch');
  }

  hasSenhaMismatchError(): boolean {
    return this.formAssessor.hasError('senhaMismatch');
  }
} 